<template>
  <Container>
    <PageHeader :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap align-items-center justify-content-between">
              <h4 class="card-title mb-0">Rules</h4>
              <b-button v-b-modal.rule-modal @click="clearRule" variant="outline-primary">Add Rules</b-button>
            </div>

            <b-modal size="lg" id="rule-modal" title="Create Rules" title-class="font-18">
              <ValidationObserver ref="form" v-slot="{ handleSubmit }">
                <b-form id="rule-form" @submit.prevent="handleSubmit(onSubmit)">
                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Start Date" label-for="start_date">
                        <ValidationProvider vid="start_date" rules="required" v-slot="{ errors }">
                          <DatePicker
                            v-model="start_date"
                            class="w-100"
                            id="start_date"
                            placeholder="Start Date"
                            format="DD-MM-YYYY"
                            :disabled-date="disabledAfterEndDate"
                          ></DatePicker>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="End Date" label-for="end_date">
                        <ValidationProvider vid="end_date" rules="required" v-slot="{ errors }">
                          <DatePicker
                            v-model="end_date"
                            class="w-100"
                            id="end_date"
                            placeholder="End Date"
                            format="DD-MM-YYYY"
                            :disabled-date="disabledBeforeStartDate"
                          ></DatePicker>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-form-group label="Title" label-for="title">
                    <ValidationProvider rules="required|min:2" v-slot="{ errors }">
                      <b-form-input v-model="rule.title" id="title" placeholder="Enter title"></b-form-input>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>

                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Min days between booking & arrival" label-for="days_before">
                        <ValidationProvider rules="required|minimum:0" v-slot="{ errors }">
                          <b-form-input
                            v-model="rule.days_before"
                            type="number"
                            id="days_before"
                            placeholder="Enter number of days"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Minimum stay" label-for="minimum_stay">
                        <ValidationProvider rules="required|minimum:1" v-slot="{ errors }">
                          <b-form-input
                            v-model="rule.minimum_stay"
                            type="number"
                            id="minimum_stay"
                            placeholder="Enter minimum stay"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Charge after guests" label-for="charge_after_guests">
                        <ValidationProvider rules="required|minimum:2" v-slot="{ errors }">
                          <b-form-input
                            v-model="rule.charge_after_guests"
                            type="number"
                            id="charge_after_guests"
                            placeholder="Enter number of guests"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Charge per extra guest" label-for="extra_guest_cost">
                        <ValidationProvider rules="required|minimum:0" v-slot="{ errors }">
                          <b-input-group class="w-100">
                            <b-form-input
                              v-model="rule.extra_guest_cost"
                              type="number"
                              step=".01"
                              id="extra_guest_cost"
                              placeholder="Enter cost"
                            ></b-form-input>

                            <template #append>
                              <b-form-select
                                v-model="rule.percentage"
                                :options="[
                                  { value: false, text: '$' },
                                  { value: true, text: '%' },
                                ]"
                              >
                              </b-form-select>
                            </template>
                          </b-input-group>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-form-group label="Checkin Days" label-for="checkin_days">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <Multiselect
                        id="checkin_days"
                        v-model="checkin_days"
                        :options="daysOptions"
                        track-by="value"
                        label="text"
                        :multiple="true"
                        :close-on-select="false"
                        deselect-label="Unselect"
                      ></Multiselect>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>

                  <b-form-group label="Checkout Days" label-for="checkout_days">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <Multiselect
                        id="checkout_days"
                        v-model="checkout_days"
                        :options="daysOptions"
                        track-by="value"
                        label="text"
                        :multiple="true"
                        :close-on-select="false"
                        deselect-label="Unselect"
                      ></Multiselect>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>

                  <b-form-group label="Lodgings" label-for="lodgings">
                    <ValidationProvider vid="regulations.lodging" v-slot="{ errors }">
                      <Multiselect
                        id="lodgings"
                        v-model="lodging_ids"
                        :options="lodgingsName"
                        track-by="value"
                        label="text"
                        :multiple="true"
                        :close-on-select="false"
                        deselect-label="Unselect"
                        @select="addRemovedRegulation"
                        @remove="removeRegulation"
                      ></Multiselect>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-form>
              </ValidationObserver>

              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('rule-modal')">Close</b-button>
                <b-button form="rule-form" type="submit" variant="primary">Save</b-button>
              </template>
            </b-modal>

            <div class="row mt-4">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length mb-3">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select class="mx-1" v-model="perPage" size="sm" :options="pageOptions"></b-form-select
                    >&nbsp;entries
                  </label>
                </div>
              </div>

              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_filter" class="dataTables_filter text-md-right">
                  <label class="d-inline-flex align-items-center">
                    Search:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                      class="form-control form-control-sm ml-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <b-table
                :items="ruleItems"
                :fields="fields"
                responsive="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                @filtered="onFiltered"
              >
                <template v-slot:cell(actions)="row">
                  <b-dropdown variant="default" dropleft>
                    <template v-slot:button-content>
                      <i class="fas fa-bars"></i>
                      <i class="bx bx-caret-down"></i>
                    </template>

                    <b-dropdown-item v-b-modal.rule-modal @click="toggleEdit(row.item.id)">
                      <i class="fa fa-pencil-alt"></i>
                      Edit
                    </b-dropdown-item>

                    <b-dropdown-item :to="{ path: 'rules/' + row.item.id }">
                      <i class="fa fa-info-circle"></i>
                      Details
                    </b-dropdown-item>

                    <b-dropdown-item link-class="text-danger">
                      <i class="fas fa-trash"></i>
                      Delete
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Container>
</template>

<script>
import Container from "../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { getLodgings, getRules, createRule, updateRule } from "@/store/api";
import moment from "moment";

export default {
  page: {
    title: "Rules",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Container,
    PageHeader,
  },

  data() {
    return {
      daysOptions: [
        { text: "Sun", value: 0 },
        { text: "Mon", value: 1 },
        { text: "Tue", value: 2 },
        { text: "Wed", value: 3 },
        { text: "Thu", value: 4 },
        { text: "Fri", value: 5 },
        { text: "Sat", value: 6 },
      ],
      rule: {
        start_date: "",
        end_date: "",
        title: "",
        minimum_stay: "",
        checkin_days: [],
        checkout_days: [],
        charge_after_guests: "",
        extra_guest_cost: "",
        percentage: false,
        days_before: "",
        regulations_attributes: [],
      },
      start_date: "",
      end_date: "",
      lodging_ids: [],
      checkin_days: [
        { text: "Sun", value: 0 },
        { text: "Mon", value: 1 },
        { text: "Tue", value: 2 },
        { text: "Wed", value: 3 },
        { text: "Thu", value: 4 },
        { text: "Fri", value: 5 },
        { text: "Sat", value: 6 },
      ],
      checkout_days: [
        { text: "Sun", value: 0 },
        { text: "Mon", value: 1 },
        { text: "Tue", value: 2 },
        { text: "Wed", value: 3 },
        { text: "Thu", value: 4 },
        { text: "Fri", value: 5 },
        { text: "Sat", value: 6 },
      ],
      lodgings: [],
      rules: [],
      update: false,
      ruleId: "",
      items: [
        {
          text: "Home",
          to: "/",
        },
        {
          text: "Settings",
          to: "/settings",
        },
        {
          text: "Rules",
          active: true,
        },
      ],
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: "",
      sortDesc: false,
      fields: [
        { key: "title" },
        { key: "start_date" },
        { key: "end_date" },
        { key: "minimum_stay" },
        { key: "checkin_days" },
        { key: "checkout_days" },
        { key: "charge_after_guests" },
        { key: "extra_guest_cost" },
        { key: "days_before" },
        { key: "actions", tdClass: "text-right" },
      ],
    };
  },

  created() {
    getLodgings().then((response) => (this.lodgings = response));
    getRules().then((response) => (this.rules = response));
  },

  mounted() {
    this.totalRows = this.items.length;
  },

  computed: {
    rows() {
      return this.lodgingItems.length;
    },

    lodgingsName() {
      return this.lodgings.map((lodging) => ({ value: lodging.id, text: lodging.attributes.name }));
    },

    ruleItems() {
      return this.rules.map((rule) => {
        return {
          id: rule.id,
          title: rule.attributes.title,
          start_date: moment(rule.attributes.start_date).format("MMM DD, YYYY"),
          end_date: moment(rule.attributes.end_date).format("MMM DD, YYYY"),
          minimum_stay: rule.attributes.minimum_stay,
          checkin_days: this.convertToWeekDay(rule.attributes.checkin_days),
          checkout_days: this.convertToWeekDay(rule.attributes.checkout_days),
          charge_after_guests: rule.attributes.charge_after_guests,
          extra_guest_cost: `${rule.attributes.extra_guest_cost}${rule.attributes.percentage ? "%" : "$"}`,
          days_before: rule.attributes.days_before,
        };
      });
    },
  },

  methods: {
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    disabledBeforeStartDate(date) {
      return date <= this.start_date;
    },

    disabledAfterEndDate(date) {
      if (!this.end_date) return false;
      return date >= this.end_date;
    },

    removeRegulation(lodging) {
      this.rule.regulations_attributes.push({ id: lodging.id, lodging_id: lodging.value, _destroy: true });
    },

    addRemovedRegulation(lodging) {
      let regulations = this.rule.regulations_attributes.filter((regulation) => regulation.lodging_id == lodging.value);
      if (regulations.length != 0) {
        lodging.id = regulations[0].id;
        this.rule.regulations_attributes = this.rule.regulations_attributes.filter(
          (regulation) => regulation.lodging_id != lodging.value
        );
      }
    },

    clearRule() {
      this.update = false;
      this.start_date = "";
      this.end_date = "";
      this.checkin_days = this.daysOptions;
      this.checkout_days = this.daysOptions;
      this.rule.title = "";
      this.rule.minimum_stay = "";
      this.rule.checkin_days = [];
      this.rule.checkout_days = [];
      this.rule.charge_after_guests = "";
      this.rule.extra_guest_cost = "";
      this.rule.percentage = false;
      this.rule.days_before = "";
      this.rule.regulations_attributes = [];
      this.lodging_ids = [];
    },

    convertToWeekDay(weekDays) {
      let days = "";
      for (let day of weekDays) {
        days += moment().day(day).format("ddd") + " ";
      }
      return days;
    },

    toggleEdit(id) {
      this.clearRule();
      this.update = true;
      this.ruleId = id;
      let selectedRule = this.rules.find((rule) => rule.id === id);
      this.start_date = moment(selectedRule.attributes.start_date)._d;
      this.end_date = moment(selectedRule.attributes.end_date)._d;
      this.rule.title = selectedRule.attributes.title;
      this.rule.minimum_stay = selectedRule.attributes.minimum_stay;
      this.checkin_days = selectedRule.attributes.checkin_days.map((day) => {
        return { text: moment().day(day).format("ddd"), value: day, };
      });
      this.checkout_days = selectedRule.attributes.checkout_days.map((day) => {
        return { text: moment().day(day).format("ddd"), value: day, };
      });
      this.rule.charge_after_guests = selectedRule.attributes.charge_after_guests;
      this.rule.extra_guest_cost = selectedRule.attributes.extra_guest_cost;
      this.rule.percentage = selectedRule.attributes.percentage;
      this.rule.days_before = selectedRule.attributes.days_before;
      this.lodging_ids = selectedRule.attributes.regulations.data.map((regulation) => {
        return {
          text: regulation.attributes.lodging.name,
          value: regulation.attributes.lodging.id,
          id: regulation.id,
          _destroy: false,
        };
      });
    },

    onSubmit() {
      this.rule.start_date = moment(this.start_date).format("YYYY-MM-DD");
      this.rule.end_date = moment(this.end_date).format("YYYY-MM-DD");
      let regulations = this.lodging_ids.map((lodging_id) => {
        return { id: lodging_id.id, lodging_id: lodging_id.value };
      });
      this.rule.checkin_days = this.checkin_days.map((day) => {
        return day.value;
      });
      this.rule.checkout_days = this.checkout_days.map((day) => {
        return day.value;
      });
      this.rule.regulations_attributes = this.rule.regulations_attributes.concat(regulations);
      if (this.update) {
        updateRule(this.rule, this.ruleId)
          .then((response) => {
            this.$bvModal.hide("rule-modal");
            let index = this.rules.findIndex((rule) => rule.id === this.ruleId);
            this.rules.splice(index, 1);
            this.rules.splice(index, 0, response);
          })
          .catch((error) => {
            this.$refs.form.setErrors(error.response.data.errors);
            this.rule.regulations_attributes = [];
          });
      } else {
        createRule(this.rule)
          .then((response) => {
            this.$bvModal.hide("rule-modal");
            this.rules.push(response);
          })
          .catch((error) => {
            this.$refs.form.setErrors(error.response.data.errors);
            this.rule.regulations_attributes = [];
          });
      }
    },
  },
};
</script>
